import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import FilterListIcon from "@mui/icons-material/FilterList";
import { Badge, Box, Button, Popover, Tooltip } from "@mui/material";
import { useGridApiContext } from "@mui/x-data-grid";

import { populateMultiFilterFromSearchParams } from "./filters/FiltersHelper";
import MultiFilterPanel from "./filters/MultiFilterPanel";

export type FilterItem = {
  id: number;
  field: string;
  operator: string;
  value?: any;
  isValid: boolean;
};

type FiltersSummary = {
  filterDescriptions: string[];
  activeFilterCount: number;
};

const JobsMultiFilterButton: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const gridApiRef = useGridApiContext();
  const columns = useMemo(() => gridApiRef.current.getAllColumns().filter((x) => x.filterable), [gridApiRef]);
  const [activeFilters, setActiveFilters] = useState<FilterItem[]>(
    populateMultiFilterFromSearchParams(searchParams),
  );

  const findColumn = (field: string) => columns.find((col) => col.field === field)!;

  const refreshFiltersSummary = (filters: FilterItem[]) => {
    const filterDescriptions = filters.map((filter) => {
      const column = findColumn(filter.field);
      return `${column.headerName} ${column.filterOperators![0].label!.toLowerCase()} ${filter.value}`;
    });
    return { filterDescriptions, activeFilterCount: filterDescriptions.length };
  };

  const [filtersSummary, setFiltersSummary] = useState<FiltersSummary>(refreshFiltersSummary(activeFilters));

  const renderFiltersDescription = (filterDescriptions: string[]): JSX.Element => {
    const filterCount = filterDescriptions.length;
    const filterLabel = filterCount === 1 ? "filter" : "filters";

    return (
      <Box>
        {filterCount} active {filterLabel}
        <ul style={{ paddingLeft: 18 }}>
          {filterDescriptions.map((desc, index) => (
            <li key={`${desc}-${index}`}>{desc}</li>
          ))}
        </ul>
      </Box>
    );
  };

  const handleApplyFilter = (filters: FilterItem[]) => {
    const filterQueries = filters
      .map((filter) => findColumn(filter.field).filterOperators![0].getValueAsString!(filter.value))
      .join("&");

    const query = new URLSearchParams(filterQueries);
    query.append("page", "1");
    query.append("limit", searchParams.get("limit") || "25");

    setSearchParams(query);
    setActiveFilters(filters);
    setFiltersSummary(refreshFiltersSummary(filters));
    handleClose();
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip
        id="grid-filters-button-tooltip"
        title={renderFiltersDescription(filtersSummary.filterDescriptions)}
        placement="top"
      >
        <Button
          size="small"
          startIcon={
            <Badge showZero={false} badgeContent={filtersSummary.activeFilterCount} color="primary">
              <FilterListIcon />
            </Badge>
          }
          onClick={handleClick}
          data-testid="grid-filters-button"
        >
          Filters
        </Button>
      </Tooltip>

      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <MultiFilterPanel
          initialFilters={activeFilters}
          onApplyFilters={handleApplyFilter}
          onClearFilters={() => handleApplyFilter([])}
          data-testid="multi-filter-panel"
        />
      </Popover>
    </>
  );
};

export default JobsMultiFilterButton;
